import { useTranslation } from "react-i18next";

/**
 * A small presentational frame for one section of the rail.
 *
 * This is NOT `WorklistSection` — the worklist frame publishes its row count into
 * `WorklistContext` because worklist rows are numbered running across sections and
 * feed the page totals; the rail is not part of that numbering and has no count
 * badge, so reusing it would wire the rail into arithmetic it does not belong in.
 * The three states are kept visually distinct for the same reason as everywhere
 * else on this page: a failed load must never read as a free afternoon.
 */

export interface RailSectionProps {
	titleKey: string;
	isLoading: boolean;
	isError: boolean;
	isEmpty: boolean;
	errorMessageKey: string;
	emptyMessageKey: string;
	testId: string;
	children: React.ReactNode;
}

const RailSection = (props: RailSectionProps) => {
	const { t } = useTranslation("common");

	return (
		<section
			className="rail-section"
			data-testid={props.testId}
		>
			<h3 className="rail-section__title">{t(props.titleKey)}</h3>

			{props.isLoading && (
				<p className="rail-section__message loading">
					<i className="pi pi-spin pi-spinner message-icon"></i>
					{t("PARTICIPANT.DASHBOARD.REQUIRES_ATTENTION.MESSAGES.LOADING")}
				</p>
			)}

			{!props.isLoading && props.isError && (
				<p
					className="rail-section__message has-error"
					role="alert"
				>
					{t(props.errorMessageKey)}
				</p>
			)}

			{!props.isLoading && !props.isError && props.isEmpty && (
				<p className="rail-section__message">{t(props.emptyMessageKey)}</p>
			)}

			{!props.isLoading && !props.isError && !props.isEmpty && props.children}
		</section>
	);
};

export default RailSection;
